<script setup lang="ts">
    import { computed } from 'vue';
    import { Bar } from 'vue-chartjs';
    import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip } from 'chart.js';
    import { applyChartDefaults } from '@shared/modules/chartjs-defaults';
    import { useRouter, useRoute } from 'vue-router';
    import { IssueReportCounts, TrackingOptions } from '../../interfaces';
    import { STATISTICS_EVENT_TYPES, getStatisticsLabelMap } from './statistics-types';
    import BsAlert from '@shared/components/BsAlert.vue';
    import { AlertTypes } from '@shared/components/BsAlerts';
    import { chartColor } from '@shared/modules/chart-colors';

    type CountKey = keyof IssueReportCounts;
    const countKeys: Record<string, CountKey> = {
        sent: 'SentCount',
        received: 'ReceivedCount',
        openedorclicked: 'OpenClickCount',
        opened: 'OpenCount',
        clicked: 'ClickCount',
        conversion: 'ConversionCount',
        bounced: 'BounceCount',
        unsubscribed: 'UnsubscribeCount',
        inactive: 'InactiveCount',
    };
    const pctKeys: Record<string, CountKey> = {
        sent: 'SentPercentage',
        received: 'ReceivedPercentage',
        openedorclicked: 'OpenClickPercentage',
        opened: 'OpenPercentage',
        clicked: 'ClickPercentage',
        conversion: 'ConversionPercentage',
        bounced: 'BouncePercentage',
        unsubscribed: 'UnsubscribePercentage',
        inactive: 'InactivePercentage',
    };

    ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip);
    applyChartDefaults();

    const props = defineProps<{
        overview: IssueReportCounts | null;
        trackingOptions?: TrackingOptions;
        isAnonymousTracking: boolean;
    }>();

    const router = useRouter();
    const route = useRoute();

    const visibleStatisticsEventTypes =
        props.trackingOptions === TrackingOptions.NoTracking ? STATISTICS_EVENT_TYPES.filter((t) => t.key === 'sent') : STATISTICS_EVENT_TYPES;

    const contactTypes = visibleStatisticsEventTypes.map((t) => t.contactType);
    const barColors = visibleStatisticsEventTypes.map((t) => chartColor(t.color));
    const allNavigableTypes = new Set(visibleStatisticsEventTypes.filter((t) => t.navigable !== false).map((t) => t.contactType));
    const anonymousNavigableTypes = new Set(['Sent', 'Bounced', 'Unsubscribed']);
    const navigableTypes = computed(() => (props.isAnonymousTracking ? anonymousNavigableTypes : allNavigableTypes));

    function fmt(val: number): string {
        return val > 0 && val < 1 ? val.toFixed(1) : Math.round(val).toString();
    }

    function navigateToContacts(index: number) {
        if (index < 0 || index >= contactTypes.length) return;
        if (!navigableTypes.value.has(contactTypes[index])) return;
        router.push({
            name: 'IssueStatisticsContacts',
            params: { issueId: route.params.issueId, contactType: contactTypes[index] },
            query: { ...route.query },
        });
    }

    function getLabelIndexAtEvent(chart: any, event: any): number {
        const evt = event.native as MouseEvent;
        if (!evt || !chart) return -1;
        const xScale = chart.scales.x;
        const rect = chart.canvas.getBoundingClientRect();
        const x = evt.clientX - rect.left;
        const y = evt.clientY - rect.top;
        if (y >= xScale.top && y <= xScale.bottom) {
            return xScale.getValueForPixel(x) as number;
        }
        return -1;
    }

    const labelPointerPlugin = {
        id: 'labelPointer',
        afterEvent(chart: any, args: any) {
            const { type } = args.event;
            const index = getLabelIndexAtEvent(chart, args.event);
            if (type === 'mousemove' && index >= 0) {
                chart.canvas.style.cursor = navigableTypes.value.has(contactTypes[index]) ? 'pointer' : 'default';
            } else if (type === 'click' && index >= 0) {
                navigateToContacts(index);
            }
        },
    };

    const chartData = computed(() => {
        if (!props.overview) return { labels: [], datasets: [] };
        const o = props.overview;

        const labelMap = getStatisticsLabelMap();
        const rows = visibleStatisticsEventTypes.map((t) => ({
            label: labelMap[t.key],
            count: o[countKeys[t.key]],
            pct: o[pctKeys[t.key]],
        }));

        return {
            labels: rows.map((r) => [r.label, `${r.count} (${fmt(r.pct)}%)`]),
            datasets: [
                {
                    data: rows.map((r) => parseFloat(fmt(r.pct))),
                    backgroundColor: barColors,
                    categoryPercentage: 0.65,
                },
            ],
        };
    });

    const chartOptions = computed(() => ({
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: { display: false },
            tooltip: { callbacks: { label: () => '' } },
        },
        scales: {
            x: {
                grid: { display: false },
                ticks: { font: { weight: 'bold' as const } },
            },
            y: {
                min: 0,
                max: 100,
                grid: { color: chartColor('grid'), lineWidth: (ctx: any) => (ctx.tick.value === 0 ? 2 : 1) },
                ticks: { callback: (value: number | string) => value + '%' },
            },
        },
        onClick: (event: unknown, elements: { index: number }[], chart: any) => {
            if (elements.length) {
                navigateToContacts(elements[0].index);
                return;
            }
            navigateToContacts(getLabelIndexAtEvent(chart, event));
        },
    }));
</script>

<template>
    <div class="print-block">
        <h2 class="h3 m-xs-b">{{ $Resources.IssueStatisticsOverviewHeading }}</h2>
        <p>{{ $Resources.IssueStatisticsOverviewDescription }}</p>
        <BsAlert
            v-if="isAnonymousTracking"
            class="m-sm-b"
            :type="AlertTypes.Info"
            :dismissible="false"
            :content="$Resources.IssueStatisticsAnonymizedTrackingDisabledInfo" />
        <div class="chart-container chart-container-panel h-400">
            <Bar :data="chartData" :options="chartOptions" :plugins="[labelPointerPlugin]" />
        </div>
    </div>
</template>

